import { fmtDateTime } from "../lib/format";
import { useEffect, useState, useRef, useCallback } from "react";
import { PortalLink } from "../components/PortalLink";
import { usePortalNavigate } from "../hooks/usePortalNavigate";
import { theme, card, emptyFrame, btnPrimary, inputStyle } from "../theme";
import { IndexTabs, RulerProgress, StatusTag, type StatusTone } from "../components/Drawing";
import { api, apiStream, ApiError } from "../api/client";
import { TierLockModal, TierErrorPayload, TIER_NAMES } from "../components/TierLockModal";
import { useFrameworks, FRAMEWORK_LABELS, CE_FRAMEWORK_IDS } from "../context/FrameworksContext";
import { controlRoute, frameworkOfControl, frameworkOfSentence, scopeSummaryText } from "../utils/controlRoute";

interface CheckRun {
  id: string;
  status: "queued" | "running" | "cancelling" | "cancelled" | "done" | "error";
  overallScore: number | null;
  resultJson: ResultJson | null;
  progressJson: {
    frameworks: string[];
    completed: string[];
    current: string | null;
    // Present once the worker has sized the current standard: which model call is in flight.
    pass?: { index: number; total: number };
    sizing?: { inputTokens: number; budgetTokens: number; passes: number; evidenceBatches: number; documents: number };
    // Present while a scheduled check waits on its Message Batch (backend worker.ts): half price, slower.
    batch?: { requests: number; submittedAt: string };
  } | null;
  errorMessage: string | null;
  createdAt: string;
  completedAt: string | null;
  /** Empty = a whole-organisation check; otherwise the sites it assessed (backend lib/siteStandards.ts). */
  siteIds?: string[];
}

/** A site and the standards that apply there, from GET /site-compliance. */
interface SiteOption {
  site: { id: string; name: string; inScope: boolean };
  standards: Array<{ frameworkId: string; targetLevel: string; targetScore: number | null }>;
}

interface ResultJson {
  overallScore?: number;
  summary?: string;
  summaries?: Record<string, string>;
  keyFindings?: string[];
  perControl?: ControlResult[];
  // Frameworks (and pseudo-frameworks such as "legal") this run assessed. Absent on older runs.
  // On a site-scoped run each is "<frameworkId>@<siteId>", as are the keys of summaries.
  frameworkIds?: string[];
  // The sites a site-scoped run assessed.
  sites?: Array<{ id: string; name: string }>;
}

interface ControlResult {
  controlId: string;
  // Framework the worker assessed this control under. Absent on older runs.
  frameworkId?: string;
  // The site this verdict is for, on a site-scoped run.
  siteId?: string;
  name?: string;
  status: "COMPLIANT" | "PARTIAL" | "GAP" | "NOT_APPLICABLE" | "NOT_STARTED";
  evidenceFound?: string[];
  gaps?: string[];
  recommendations?: string[];
  /** Set when the verdict turns on the organisation's own adopted policy (backend/src/lib/policyAdoption.ts). */
  policyConflict?: string;
  /** Set when nothing this control is judged on changed, so its earlier verdict was kept: the date it was produced (backend/src/lib/complianceReuse.ts). */
  carriedFrom?: string;
}

interface RemediationStep {
  action: string;
  ownerRole: string;
  effortDays: number;
  priority: "immediate" | "short-term" | "medium-term";
  category: "policy" | "technical" | "training" | "process";
}

interface QueueHealth {
  workerOnline: boolean;
  workerCount: number;
  queued: number;
  active: number;
  failed: number;
  redisUnavailable?: boolean;
}

const AVAILABLE_STANDARDS = [
  { id: "iso27001",              label: "ISO 27001:2022",       sub: "Information security management" },
  { id: "cyber_essentials",      label: "Cyber Essentials",     sub: "NCSC foundational controls" },
  { id: "cyber_essentials_plus", label: "Cyber Essentials Plus",    sub: "CE with independent verification" },
  { id: "iso42001",              label: "ISO 42001:2023",       sub: "AI management systems" },
  { id: "iso9001",               label: "ISO 9001:2015",        sub: "Quality management" },
  { id: "soc2",                  label: "SOC 2",                sub: "Trust services criteria" },
  { id: "pci_dss",               label: "PCI DSS v4.0",         sub: "Payment card industry security" },
  { id: "iasme_ca_sole_trader",  label: "IASME CA (Sole Trader)",  sub: "IASME Cyber Assurance, 1–2 people" },
  { id: "iasme_ca_micro",        label: "IASME CA (Micro)",        sub: "IASME Cyber Assurance, 3–9 people" },
  { id: "iasme_ca_small",        label: "IASME CA (Small)",        sub: "IASME Cyber Assurance, 10–49 people" },
  { id: "iasme_ca_medium_large", label: "IASME CA (Medium/Large)", sub: "IASME Cyber Assurance, 50+ people" },
  { id: "iso45001",              label: "ISO 45001:2018",       sub: "Occupational health and safety" },
  { id: "iso14001",              label: "ISO 14001:2015",       sub: "Environmental management" },
  { id: "ncsc_caf",              label: "NCSC CAF v4.0",        sub: "Cyber Assessment Framework, 41 outcomes" },
] as const;

/** A run key: a framework id, or "<frameworkId>@<siteId>" on a site-scoped run. */
function parseRunKey(key: string): { fwId: string; siteId: string | null } {
  const at = key.indexOf("@");
  return at < 0 ? { fwId: key, siteId: null } : { fwId: key.slice(0, at), siteId: key.slice(at + 1) };
}

/** Display label for any framework, pseudo-framework or framework-at-site key that can appear in a run. */
function standardLabel(key: string, siteNames?: ReadonlyMap<string, string>): string {
  const { fwId, siteId } = parseRunKey(key);
  const base = fwId === "legal"
    ? "Legal & Regulatory Obligations"
    : AVAILABLE_STANDARDS.find((s) => s.id === fwId)?.label
      ?? FRAMEWORK_LABELS[fwId as keyof typeof FRAMEWORK_LABELS]
      ?? fwId;
  return siteId ? `${base} at ${siteNames?.get(siteId) ?? "a site"}` : base;
}

const CE_FAMILY: ReadonlySet<string> = CE_FRAMEWORK_IDS;

/** Where a verdict's remediation advice is kept: per control, and per site on a site-scoped run. */
function adviceKey(c: ControlResult): string {
  return c.siteId ? `${c.controlId}@${c.siteId}` : c.controlId;
}

/** Framework a result entry belongs to: the recorded one, else inferred from the ID. */
function frameworkOfResult(c: ControlResult): string | null {
  return c.frameworkId ?? frameworkOfControl(c.controlId);
}

const STATUS_COLORS: Record<string, string> = {
  COMPLIANT:   theme.color.success,
  GAP:         theme.color.danger,
  NOT_STARTED: theme.color.subtle,
  PARTIAL:     theme.color.warning,
};

const STATUS_LABELS: Record<string, string> = {
  COMPLIANT:   "Compliant",
  GAP:         "Gap",
  NOT_STARTED: "Not assessed",
  PARTIAL:     "In progress",
};

/** The result card's counts ("140 compliant · 12 gaps"): the row badge's wording reads oddly after a number. */
const SUMMARY_LABELS: Record<"COMPLIANT" | "PARTIAL" | "GAP" | "NOT_STARTED", string> = {
  COMPLIANT:   "compliant",
  PARTIAL:     "in progress",
  GAP:         "gaps",
  NOT_STARTED: "not assessed",
};

/** A run's status as a word ("Done", "Queued"), never the stored value. */
const RUN_STATUS_LABELS: Record<string, string> = {
  queued: "Queued", running: "Running", cancelling: "Cancelling", cancelled: "Cancelled", done: "Done", error: "Failed",
};

// Older runs stored one joined narrative; split it back into per-standard sections
// by the phrases the AI uses for each standard (see FRAMEWORK_KEYWORDS).
function splitSummaryByFramework(
  summary: string,
  controls: ControlResult[],
): Array<{ fwId: string; text: string }> | null {
  if (!summary || controls.length === 0) return null;

  const sentences = summary.split(/(?<=[.!?])\s+/).filter(Boolean);
  const sections: Record<string, string[]> = {};

  for (const sentence of sentences) {
    const id = frameworkOfSentence(sentence);
    if (id) (sections[id] ??= []).push(sentence);
  }

  const result = Object.entries(sections)
    .filter(([, sents]) => sents.length > 0)
    .map(([fwId, sents]) => ({ fwId, text: sents.join(" ") }));

  return result.length > 1 ? result : null;
}

const STATUS_TONES: Record<string, StatusTone> = {
  COMPLIANT:   "met",
  GAP:         "gap",
  NOT_STARTED: "none",
  PARTIAL:     "partial",
};

function StatusBadge({ status }: { status: string }) {
  const label = STATUS_LABELS[status] ?? status;
  return <StatusTag tone={STATUS_TONES[status] ?? "none"}>{label}</StatusTag>;
}

// Estimate where the simulated progress bar should be for a run that has been running
// for `elapsedSec` seconds. Uses the same easing formula as the live timer so the bar
// picks up at the right position when the user returns to the page after navigating away.
function estimateSimulatedPct(status: string, elapsedSec: number): number {
  if (status === "queued") return Math.min(8, elapsedSec * 0.2);
  if (status === "running" || status === "cancelling") {
    let pct = 8;
    for (let i = 0; i < Math.min(elapsedSec, 600); i++) {
      pct += Math.max(0.3, (88 - pct) * 0.04);
    }
    return Math.min(88, pct);
  }
  return 0;
}

export function ComplianceCheckPage() {
  const { enrolled, loading: frameworksLoading } = useFrameworks();
  // Only enrolled standards can be selected; the worker refuses anything else anyway.
  const assessableStandards = AVAILABLE_STANDARDS.filter((s) => enrolled.has(s.id));
  const assessableKey = assessableStandards.map((s) => s.id).join(",");
  const [scope, setScope] = useState("");
  const [notes, setNotes] = useState("");
  // Off: controls whose evidence, context and SOA row are unchanged keep their last verdict (backend lib/complianceReuse.ts).
  const [fullReassessment, setFullReassessment] = useState(false);
  const [selectedStandards, setSelectedStandards] = useState<string[]>([]);
  // Empty = the whole organisation; otherwise the sites to assess, each against the selected
  // standards that apply there (backend lib/siteStandards.ts).
  const [siteOptions, setSiteOptions] = useState<SiteOption[]>([]);
  const [selectedSites, setSelectedSites] = useState<string[]>([]);
  const [activeRun, setActiveRun] = useState<CheckRun | null>(null);
  const cancelInFlight = useRef(false);
  const [previousRuns, setPreviousRuns] = useState<CheckRun[]>([]);
  // Until the history arrives, an empty list means "not loaded", never "no checks run".
  const [runsLoaded, setRunsLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingLabel, setLoadingLabel] = useState("Starting…");
  const [error, setError] = useState("");
  const [tab, setTab] = useState<"summary" | "gaps" | "all">("summary");
  const navigate = usePortalNavigate();
  const [tierError, setTierError] = useState<TierErrorPayload | null>(null);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const completedFrameworksRef = useRef<Set<string>>(new Set());
  const [elapsed, setElapsed] = useState(0);
  const [simulatedPct, setSimulatedPct] = useState(0);
  const [queueHealth, setQueueHealth] = useState<QueueHealth | null>(null);
  const healthCheckedRef = useRef(false);
  const [cancellingIds, setCancellingIds] = useState<Set<string>>(new Set());
  const [runsLoadError, setRunsLoadError] = useState(false);
  const [adviceByControl, setAdviceByControl] = useState<Record<string, RemediationStep[]>>({});
  const [adviceLoading, setAdviceLoading] = useState<Set<string>>(new Set());
  const [adviceExpanded, setAdviceExpanded] = useState<Set<string>>(new Set());
  // Off by default: runs are tracked by 3 s polling unless the user opts in to the SSE stream.
  const [realtimeMode, setRealtimeMode] = useState<boolean>(() => {
    try { return localStorage.getItem("abacas.realtimeUpdates") === "true"; } catch { return false; }
  });
  const sseAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => { if (sseAbortRef.current) sseAbortRef.current.abort(); };
  }, []);

  useEffect(() => {
    // Pre-fill scope from org profile
    api.get<{ organization?: { ismsScope?: string | null } }>("/organizations/profile")
      .then((d) => { if (d.organization?.ismsScope) setScope(d.organization.ismsScope); })
      .catch(() => {});

    // ?site=<id> (the site page's "Check this site") pre-selects that site.
    const preselect = new URLSearchParams(window.location.search).get("site");
    api.get<{ sites: SiteOption[] }>("/site-compliance")
      .then((d) => {
        setSiteOptions(d.sites);
        if (preselect && d.sites.some((o) => o.site.id === preselect)) setSelectedSites([preselect]);
      })
      .catch(() => setSiteOptions([]));

    loadPreviousRuns();
  }, []);

  // Pre-select every enrolled, assessable standard; drop any selection that is no
  // longer enrolled (e.g. after the org changes standards in My Services).
  useEffect(() => {
    if (frameworksLoading) return;
    const allowed = assessableKey ? assessableKey.split(",") : [];
    setSelectedStandards((prev) => {
      const kept = prev.filter((id) => allowed.includes(id));
      return kept.length > 0 ? kept : allowed;
    });
  }, [assessableKey, frameworksLoading]);

  async function loadPreviousRuns() {
    try {
      const d = await api.get<{ runs: CheckRun[] }>("/compliance/check-runs", { include: "sites" });
      setPreviousRuns(d.runs);
      setRunsLoaded(true);
      setRunsLoadError(false);
      // Resume polling if there's an in-flight run (e.g. user navigated away and back)
      const inFlight = d.runs.find((r) => ["queued", "running", "cancelling"].includes(r.status));
      if (inFlight) {
        // Restore progress state from the run's actual start time so the bar doesn't reset to 0
        const startMs = new Date(inFlight.createdAt).getTime();
        const elapsedSec = Math.round((Date.now() - startMs) / 1000);
        startTimeRef.current = startMs;
        setElapsed(elapsedSec);
        setSimulatedPct(estimateSimulatedPct(inFlight.status, elapsedSec));
        setActiveRun(inFlight);
        startTracking(inFlight.id);
      } else {
        const latest = d.runs.find((r) => r.status === "done");
        if (latest) setActiveRun(latest);
      }
    } catch {
      setRunsLoadError(true);
    }
  }

  function toggleStandard(id: string) {
    setSelectedStandards((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  }

  function addFiles(incoming: FileList | File[]) {
    const files = Array.from(incoming);
    setAttachedFiles((prev) => {
      const existing = new Set(prev.map((f) => f.name + f.size));
      return [...prev, ...files.filter((f) => !existing.has(f.name + f.size))];
    });
  }

  function removeFile(index: number) {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    setIsDragOver(true);
  }

  function handleDragLeave(e: React.DragEvent) {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragOver(false);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files.length > 0) addFiles(e.dataTransfer.files);
  }

  async function uploadAttachedFiles(): Promise<void> {
    for (let i = 0; i < attachedFiles.length; i++) {
      const file = attachedFiles[i];
      setLoadingLabel(`Uploading ${i + 1} of ${attachedFiles.length}: ${file.name}…`);
      const { blobPath, uploadUrl } = await api.post<{ blobPath: string; uploadUrl: string }>(
        "/documents/request-upload",
        { fileName: file.name, contentType: file.type || "application/octet-stream" }
      );
      await fetch(uploadUrl, {
        method: "PUT",
        headers: { "x-ms-blob-type": "BlockBlob", "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      await api.post("/documents", {
        blobPath,
        fileName: file.name,
        sizeBytes: file.size,
        tags: [],
      });
    }
  }

  function startPolling(runId: string) {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const d = await api.get<{ run: CheckRun }>(`/compliance/check-runs/${runId}`);
        setActiveRun(d.run);
        if (["done", "error", "cancelled"].includes(d.run.status)) {
          clearInterval(pollRef.current!);
          pollRef.current = null;
          loadPreviousRuns();
        }
      } catch {
        clearInterval(pollRef.current!);
        pollRef.current = null;
      }
    }, 3000);
  }

  function startRealtimeStream(runId: string) {
    if (sseAbortRef.current) sseAbortRef.current.abort();
    const controller = new AbortController();
    sseAbortRef.current = controller;

    (async () => {
      try {
        const response = await apiStream(`/compliance/check-runs/${runId}/stream`, controller.signal);
        if (!response.ok || !response.body) throw new Error("SSE unavailable");

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";
          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            try {
              const payload = JSON.parse(line.slice(6));
              if (payload.error) break;
              const run: CheckRun = payload.run;
              setActiveRun(run);
              if (["done", "error", "cancelled"].includes(run.status)) {
                loadPreviousRuns();
              }
            } catch { /* malformed event — skip */ }
          }
        }
      } catch (e) {
        // Aborted intentionally (cancel / navigation) — don't fall back
        if (e instanceof DOMException && e.name === "AbortError") return;
        // Connection error — fall back to 3-second polling
        startPolling(runId);
      }
    })();
  }

  function startTracking(runId: string) {
    if (realtimeMode) {
      startRealtimeStream(runId);
    } else {
      startPolling(runId);
    }
  }

  function toggleRealtimeMode() {
    setRealtimeMode((prev) => {
      const next = !prev;
      try { localStorage.setItem("abacas.realtimeUpdates", String(next)); } catch { /* storage unavailable */ }
      return next;
    });
  }

  async function cancelRun() {
    if (!activeRun || cancelInFlight.current) return;
    // One request per click, however fast the clicks come.
    cancelInFlight.current = true;
    try {
      // A queued run is cancelled outright ("cancelled"); a running one waits for its AI call ("cancelling").
      const res = await api.del<{ status?: CheckRun["status"] }>(`/compliance/check-runs/${activeRun.id}`);
      const status = res?.status === "cancelled" ? "cancelled" : "cancelling";
      setActiveRun((r) => r ? { ...r, status } : r);
      if (status === "cancelled") void loadPreviousRuns();
    } catch { /* ignore — polling will catch the final state */ }
    finally { cancelInFlight.current = false; }
  }

  async function cancelHistoricalRun(id: string) {
    setCancellingIds((prev) => new Set(prev).add(id));
    try {
      await api.del(`/compliance/check-runs/${id}`);
      await loadPreviousRuns();
    } catch {
      // silently remove the loading state; the list will show the unchanged status
    } finally {
      setCancellingIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
    }
  }

  async function toggleAdvice(c: ControlResult, frameworkId?: string) {
    // Per control and site: on a site-scoped run each site's gaps are its own.
    const id = adviceKey(c);
    if (adviceExpanded.has(id)) {
      setAdviceExpanded((prev) => { const s = new Set(prev); s.delete(id); return s; });
      return;
    }
    setAdviceExpanded((prev) => new Set(prev).add(id));
    if (adviceByControl[id]) return; // already fetched
    setAdviceLoading((prev) => new Set(prev).add(id));
    try {
      const d = await api.post<{ recommendations: RemediationStep[] | string[]; cached?: boolean }>("/guidance/recommendations", {
        framework: frameworkId ?? frameworkOfResult(c) ?? selectedStandards[0] ?? "",
        controlId: c.controlId,
        controlName: c.name ?? c.controlId,
        status: c.status,
        notes: c.gaps?.join("; ") ?? undefined,
        guidance: c.recommendations ?? undefined,
      });
      // Normalise: old cache may return string[]; convert to RemediationStep[] for display
      const recs: RemediationStep[] = d.recommendations.map((r) =>
        typeof r === "string"
          ? { action: r, ownerRole: "", effortDays: 0, priority: "short-term" as const, category: "process" as const }
          : r
      );
      setAdviceByControl((prev) => ({ ...prev, [id]: recs }));
    } catch {
      setAdviceByControl((prev) => ({ ...prev, [id]: [] }));
    } finally {
      setAdviceLoading((prev) => { const s = new Set(prev); s.delete(id); return s; });
    }
  }

  async function startCheck() {
    if (selectedStandards.length === 0) {
      setError("Select at least one standard to assess against.");
      return;
    }
    setError("");
    setTierError(null);
    setLoading(true);
    setActiveRun(null);
    resetProgress();
    try {
      if (attachedFiles.length > 0) {
        await uploadAttachedFiles();
        setAttachedFiles([]);
      }
      setLoadingLabel("Starting…");
      const d = await api.post<{ runId: string; status: string }>("/compliance/check-runs", {
        standards: selectedStandards,
        scope: scope || undefined,
        notes: notes || undefined,
        fullReassessment: fullReassessment || undefined,
        sites: selectedSites.length > 0 ? selectedSites : undefined,
      });
      const initial: CheckRun = { id: d.runId, status: "queued", overallScore: null, resultJson: null, progressJson: null, errorMessage: null, createdAt: new Date().toISOString(), completedAt: null };
      setActiveRun(initial);
      startTracking(d.runId);
    } catch (e) {
      if (e instanceof ApiError && e.status === 402 && e.code === "tier_locked") {
        setTierError(e.payload as unknown as TierErrorPayload);
      } else {
        setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : "Could not start compliance check.");
      }
    } finally {
      setLoading(false);
    }
  }

  const isPolling = activeRun && ["queued", "running", "cancelling"].includes(activeRun.status);

  // Drive a simulated progress bar while the job is in-flight.
  // We can't get real streaming progress from the worker, so we estimate:
  //   queued  → slowly climb to 8%
  //   running → climb to 88% (typical call: 20–90 s)
  //   done    → snap to 100%
  const pctTargetForStatus = useCallback((status: string | undefined) => {
    if (status === "running") return 88;
    if (status === "done") return 100;
    return 8; // queued
  }, []);

  useEffect(() => {
    if (!isPolling) {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
      if (activeRun?.status === "done") setSimulatedPct(100);
      return;
    }
    startTimeRef.current = startTimeRef.current ?? Date.now();
    healthCheckedRef.current = false;
    setQueueHealth(null);

    timerRef.current = setInterval(() => {
      const elapsedSec = Math.round((Date.now() - (startTimeRef.current ?? Date.now())) / 1000);
      setElapsed(elapsedSec);

      // If stuck on "queued" for 30+ seconds, fetch queue health once
      if (elapsedSec >= 30 && !healthCheckedRef.current) {
        healthCheckedRef.current = true;
        api.get<QueueHealth>("/compliance/queue-health").then(setQueueHealth).catch(() => {
          setQueueHealth({ workerOnline: false, workerCount: 0, queued: 0, active: 0, failed: 0, redisUnavailable: true });
        });
      }

      setSimulatedPct((prev) => {
        const target = pctTargetForStatus(activeRun?.status);
        if (prev >= target) return prev;
        // Ease toward target: faster when far away, slower near target
        const step = Math.max(0.3, (target - prev) * 0.04);
        return Math.min(target, prev + step);
      });
    }, 1000);

    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [isPolling, activeRun?.status, pctTargetForStatus]);

  // Reset timer when a new run starts
  function resetProgress() {
    startTimeRef.current = null;
    setElapsed(0);
    setSimulatedPct(0);
    setQueueHealth(null);
    healthCheckedRef.current = false;
    completedFrameworksRef.current = new Set();
  }
  const isDone = activeRun?.status === "done";
  const result = (activeRun?.resultJson as ResultJson | null) ?? null;

  // Show only controls belonging to the standards this run assessed. New runs are
  // tagged by the worker; for older runs fall back to the run's progress record, then
  // to the org's current enrolment. Legal obligations are org-level and always shown.
  const runFrameworks = new Set<string>((result?.frameworkIds ?? activeRun?.progressJson?.frameworks ?? []).map((k) => parseRunKey(k).fwId));
  // Names of the sites a site-scoped run assessed (stored on the run, else the current list).
  const siteNames = new Map<string, string>([
    ...siteOptions.map((o) => [o.site.id, o.site.name] as [string, string]),
    ...(result?.sites ?? []).map((s) => [s.id, s.name] as [string, string]),
  ]);
  // The selected standards that apply at each selected site: what a site-scoped check will assess.
  const siteCheckPlan = selectedSites.map((id) => {
    const opt = siteOptions.find((o) => o.site.id === id);
    const here = new Set((opt?.standards ?? []).filter((s) => s.targetLevel !== "not_applicable").map((s) => s.frameworkId));
    return { id, name: opt?.site.name ?? id, standards: selectedStandards.filter((fw) => here.has(fw)) };
  });
  const noSiteUnits = siteCheckPlan.length > 0 && siteCheckPlan.every((p) => p.standards.length === 0);
  const frameworkInScope = (fw: string | null): boolean => {
    if (!fw || fw === "legal") return true;
    const scopeSet = runFrameworks.size > 0 ? runFrameworks : new Set<string>(enrolled);
    if (scopeSet.has(fw)) return true;
    return CE_FAMILY.has(fw) && [...scopeSet].some((id) => CE_FAMILY.has(id));
  };
  const controls = (result?.perControl ?? []).filter((c) => frameworkInScope(frameworkOfResult(c)));
  const gapControls = controls.filter((c) => c.status === "GAP" || c.status === "PARTIAL" || c.status === "NOT_STARTED");

  return (
    <div className="page-pad">
      <h1 style={{ margin: "0 0 6px", fontSize: 20, fontWeight: 700, color: theme.color.heading, letterSpacing: "-0.3px" }}>
        Check My Compliance
      </h1>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <p style={{ margin: 0, fontSize: 13, color: theme.color.subtle }}>
          Run an AI-powered compliance check against one or more standards. Select the frameworks you want to assess, describe what's in scope, and optionally paste relevant policy text or notes for the AI to review.
        </p>
        <button
          onClick={toggleRealtimeMode}
          title={realtimeMode ? "Switch to standard polling (3 s interval)" : "Switch to real-time updates (server-sent events)"}
          style={{
            display: "flex", alignItems: "center", gap: 6, flexShrink: 0, marginLeft: 20,
            padding: "6px 12px", borderRadius: 3, cursor: "pointer", fontSize: 12, fontWeight: 600,
            border: `1px solid ${realtimeMode ? theme.color.accent : theme.color.borderStrong}`,
            background: realtimeMode ? theme.color.accentBg : "transparent",
            color: realtimeMode ? theme.color.accentText : theme.color.muted,
            transition: "all 0.15s",
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
          </svg>
          {realtimeMode ? "Real-time on" : "Real-time off"}
        </button>
      </div>

      {tierError && (
        <TierLockModal
          featureName="AI Compliance Check"
          requiredTierName={TIER_NAMES[tierError.requiredLevel] ?? `tier ${tierError.requiredLevel}`}
          requiredTierLevel={tierError.requiredLevel}
          currentTierLevel={tierError.currentLevel}
          onClose={() => setTierError(null)}
          onRequestUpgrade={() => navigate("/my-services")}
        />
      )}

      {/* Start form */}
      {!isPolling && !isDone && (
        <div style={{ ...card, marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, marginBottom: 16 }}>
            Start a compliance check
          </div>

          {/* Standard selector */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: theme.color.muted, display: "block", marginBottom: 8 }}>
              Standards to assess against
            </label>
            {frameworksLoading && (
              <div style={{ fontSize: 12, color: theme.color.faint }}>Loading your standards…</div>
            )}
            {!frameworksLoading && assessableStandards.length === 0 && (
              <div style={{ ...emptyFrame, fontSize: 12, color: theme.color.muted, padding: "12px 14px" }}>
                None of your enrolled standards can be AI-assessed yet.{" "}
                <PortalLink to="/my-services" style={{ color: theme.color.accentText, fontWeight: 600 }}>Enrol in a standard</PortalLink> to run a check.
              </div>
            )}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {assessableStandards.map((std) => {
                const selected = selectedStandards.includes(std.id);
                return (
                  <button
                    key={std.id}
                    onClick={() => toggleStandard(std.id)}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      padding: "8px 14px",
                      borderRadius: 3,
                      border: `1px solid ${selected ? theme.color.accent : theme.color.border}`,
                      background: selected ? theme.color.accentBg : "transparent",
                      color: selected ? theme.color.accentText : theme.color.muted,
                      fontSize: 12,
                      fontWeight: selected ? 700 : 400,
                      cursor: "pointer",
                      transition: "all 0.15s",
                      textAlign: "left",
                    }}
                  >
                    <span style={{ fontWeight: 600, color: selected ? theme.color.accentText : theme.color.text }}>
                      {std.label}
                    </span>
                    <span style={{ fontSize: 11, color: selected ? theme.color.accentText : theme.color.faint, marginTop: 2 }}>
                      {std.sub}
                    </span>
                  </button>
                );
              })}
            </div>
            {assessableStandards.length > 0 && selectedStandards.length === 0 && (
              <div style={{ fontSize: 11, color: theme.color.dangerText, marginTop: 6 }}>
                Select at least one standard.
              </div>
            )}
            {assessableStandards.length > 0 && (
              <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 8 }}>
                Only the standards you are enrolled in are shown — results and control updates stay within these standards.{" "}
                <PortalLink to="/my-services" style={{ color: theme.color.accentText }}>Manage standards</PortalLink>
              </div>
            )}
          </div>

          {/* Site selector: whole organisation, or chosen sites each against the standards that apply there */}
          {siteOptions.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, color: theme.color.muted, display: "block", marginBottom: 8 }}>
                Sites to assess
              </label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {[{ id: "", name: "Whole organisation" }, ...siteOptions.map((o) => ({ id: o.site.id, name: o.site.name }))].map((opt) => {
                  const selected = opt.id === "" ? selectedSites.length === 0 : selectedSites.includes(opt.id);
                  return (
                    <button
                      key={opt.id || "org"}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => setSelectedSites((prev) =>
                        opt.id === "" ? [] : prev.includes(opt.id) ? prev.filter((id) => id !== opt.id) : [...prev, opt.id],
                      )}
                      style={{
                        padding: "6px 12px", borderRadius: 3, fontSize: 12, cursor: "pointer",
                        border: `1px solid ${selected ? theme.color.accent : theme.color.border}`,
                        background: selected ? theme.color.accentBg : "transparent",
                        color: selected ? theme.color.accentText : theme.color.text,
                        fontWeight: selected ? 700 : 500,
                      }}
                    >
                      {opt.name}
                    </button>
                  );
                })}
              </div>
              <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 8, lineHeight: 1.5 }}>
                {selectedSites.length === 0
                  ? "The check assesses the organisation's management system and updates its Statement of Applicability."
                  : "Each site is assessed against the selected standards that apply there, on the organisation-wide evidence plus what is filed to that site. The results become the site's own standing; the organisation's Statement of Applicability is not changed."}
              </div>
              {siteCheckPlan.length > 0 && (
                <ul style={{ margin: "8px 0 0", paddingLeft: 18, fontSize: 12, color: theme.color.muted, lineHeight: 1.6 }}>
                  {siteCheckPlan.map((p) => (
                    <li key={p.id}>
                      <PortalLink to={`/sites/${p.id}`} style={{ color: theme.color.accentText, fontWeight: 600 }}>{p.name}</PortalLink>:{" "}
                      {p.standards.length > 0
                        ? p.standards.map((fw) => standardLabel(fw)).join(", ")
                        : <span style={{ color: theme.color.dangerText }}>none of the selected standards apply here</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, color: theme.color.muted, display: "block", marginBottom: 4 }}>
                Scope <span style={{ color: theme.color.faint, fontWeight: 400 }}>(what this check covers)</span>
              </label>
              <input
                style={inputStyle}
                value={scope}
                onChange={(e) => setScope(e.target.value)}
                placeholder="e.g. All IT systems and staff handling customer data"
              />
            </div>
            <div>
              <label style={{ fontSize: 12, color: theme.color.muted, display: "block", marginBottom: 4 }}>
                Paste policies or notes <span style={{ color: theme.color.faint, fontWeight: 400 }}>(optional)</span>
              </label>
              <textarea
                style={{ ...inputStyle, resize: "vertical" }}
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Paste any relevant policy text, evidence summaries, or context for the AI to review…"
              />
            </div>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer", userSelect: "none" }}>
              <input
                type="checkbox"
                checked={fullReassessment}
                onChange={(e) => setFullReassessment(e.target.checked)}
                style={{ marginTop: 2, flexShrink: 0 }}
              />
              <span style={{ fontSize: 12, color: theme.color.muted, lineHeight: 1.5 }}>
                Re-assess every control
                <span style={{ display: "block", color: theme.color.faint }}>
                  Otherwise a control whose evidence, context and Statement of Applicability entry have not changed since the last check keeps its verdict (for up to 30 days), which is faster and costs less.
                </span>
              </span>
            </label>
            <div>
              <label style={{ fontSize: 12, color: theme.color.muted, display: "block", marginBottom: 8 }}>
                Attach evidence documents <span style={{ color: theme.color.faint, fontWeight: 400 }}>(optional — added to your evidence library)</span>
              </label>
              {/* Hidden file input — triggered programmatically only */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: "none" }}
                onChange={(e) => { if (e.target.files) { addFiles(e.target.files); e.target.value = ""; } }}
              />
              {/* Drag-and-drop zone */}
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                style={{
                  border: `2px dashed ${isDragOver ? theme.color.accent : theme.color.border}`,
                  borderRadius: 3,
                  padding: "20px 16px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 8,
                  background: isDragOver ? theme.color.accentBg : "transparent",
                  transition: "all 0.15s",
                  cursor: "default",
                }}
              >
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={isDragOver ? theme.color.accentText : theme.color.faint} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                <span style={{ fontSize: 13, color: isDragOver ? theme.color.accentText : theme.color.muted }}>
                  Drag files here, or
                </span>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    padding: "7px 16px",
                    borderRadius: 3,
                    border: `1px solid ${theme.color.borderStrong}`,
                    background: theme.color.panel,
                    color: theme.color.text,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Choose files
                </button>
              </div>

              {/* Selected files list */}
              {attachedFiles.length > 0 && (
                <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                  {attachedFiles.map((f, i) => (
                    <div key={i} style={{
                      display: "flex", alignItems: "center", gap: 8, padding: "6px 10px",
                      background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`,
                      borderRadius: 3,
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={theme.color.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14 2 14 8 20 8"/>
                      </svg>
                      <span style={{ fontSize: 12, color: theme.color.text, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                      <span style={{ fontSize: 11, color: theme.color.faint, flexShrink: 0 }}>
                        {f.size < 1024 ? `${f.size} B` : f.size < 1024 * 1024 ? `${(f.size / 1024).toFixed(1)} KB` : `${(f.size / 1024 / 1024).toFixed(1)} MB`}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeFile(i)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: theme.color.faint, padding: "0 2px", fontSize: 16, lineHeight: 1, flexShrink: 0 }}
                        title="Remove"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <button
                onClick={startCheck}
                disabled={loading || selectedStandards.length === 0 || noSiteUnits}
                style={{ ...btnPrimary, opacity: (loading || selectedStandards.length === 0 || noSiteUnits) ? 0.6 : 1 }}
              >
                {loading ? loadingLabel : "Run Compliance Check"}
              </button>
            </div>
            {error && <div style={{ fontSize: 12, color: theme.color.dangerText }}>{error}</div>}
          </div>
        </div>
      )}

      {/* Polling state — animated progress */}
      {isPolling && (() => {
        const status = activeRun?.status ?? "queued";
        const displayPct = Math.round(simulatedPct);
        const isQueued = status === "queued";
        const isRunning = status === "running";
        const isCancelling = status === "cancelling";
        const workerOffline = queueHealth && !queueHealth.workerOnline;

        const PHASES: Array<{ key: string; label: string; active: boolean; done: boolean }> = [
          { key: "queued",  label: "Queued",     active: isQueued,    done: isRunning || isCancelling },
          { key: "running", label: "Running",     active: isRunning,   done: false },
          { key: "done",    label: "Complete",    active: isCancelling, done: false },
        ];

        function fmtElapsed(s: number) {
          if (s < 60) return `${s}s`;
          return `${Math.floor(s / 60)}m ${s % 60}s`;
        }

        return (
          <div style={{ ...card, marginBottom: 20 }}>
            {/* Phase dots */}
            <div style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: 20 }}>
              {PHASES.map((p, i) => (
                <div key={p.key} style={{ display: "flex", alignItems: "center", flex: i < PHASES.length - 1 ? 1 : undefined }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 12, fontWeight: 700, flexShrink: 0,
                      background: p.done ? theme.color.success : p.active ? (workerOffline ? theme.color.warning : theme.color.accent) : theme.color.border,
                      color: (p.done || p.active) ? "#fff" : theme.color.faint,
                      boxShadow: p.active ? `0 0 0 3px ${workerOffline ? theme.color.warningBg : theme.color.accentBg}` : "none",
                      transition: "all 0.4s",
                    }}>
                      {p.done ? "✓" : i + 1}
                    </div>
                    <span style={{ fontSize: 11, fontWeight: p.active ? 700 : 400, color: p.active ? theme.color.heading : theme.color.faint, whiteSpace: "nowrap" }}>
                      {p.label}
                    </span>
                  </div>
                  {i < PHASES.length - 1 && (
                    <div style={{ flex: 1, height: 2, background: p.done ? theme.color.success : theme.color.border, margin: "0 6px", marginBottom: 18, transition: "background 0.4s" }} />
                  )}
                </div>
              ))}
            </div>

            {/* Progress bar */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading }}>
                  {isQueued ? "Waiting for worker…" : "Running compliance check…"}
                </span>
                <span style={{ fontSize: 22, fontWeight: 800, color: workerOffline ? theme.color.warning : theme.color.accentText, fontVariantNumeric: "tabular-nums" }}>
                  {displayPct}%
                </span>
              </div>
              <RulerProgress value={displayPct} label="Compliance check progress" />
            </div>

            {/* Per-framework progress (shown while running) */}
            {isRunning && (() => {
              const progress = activeRun?.progressJson;
              const fwList = progress?.frameworks ?? [];
              const serverCompleted = progress?.completed ?? [];
              // Merge server's completed set into our locked ref — never shrink it
              serverCompleted.forEach((fwId) => completedFrameworksRef.current.add(fwId));
              const completed = completedFrameworksRef.current;
              const current = progress?.current ?? null;
              const pass = progress?.pass;
              const sizing = progress?.sizing;
              if (progress?.batch) {
                // The standards are re-run when the batch comes back, so nothing is "done" yet.
                completedFrameworksRef.current.clear();
                return (
                  <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 12, lineHeight: 1.5 }}>
                    This scheduled check has been sent to the AI as a batch, which costs half as much but can take
                    up to a few hours (usually under one). Results will appear here when it finishes. Starting a
                    check yourself means cancelling this one first.
                  </div>
                );
              }
              // Only worth a word when a standard is too large for one request and is being sent in passes.
              const passLabel = pass && pass.total > 1
                ? `analysing… pass ${pass.index} of ${pass.total}${sizing ? ` (${sizing.documents} document${sizing.documents === 1 ? "" : "s"}, ${Math.round(sizing.inputTokens / 1000)}k tokens, in ${sizing.evidenceBatches > 1 ? `${sizing.evidenceBatches} evidence batches` : `${sizing.passes} parts`})` : ""}`
                : "analysing…";
              if (fwList.length === 0) return null;
              return (
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
                  {fwList.map((fwId) => {
                    const label = standardLabel(fwId, siteNames);
                    const isDone = completed.has(fwId);
                    const isActive = fwId === current && !isDone;
                    const isPending = !isDone && !isActive;
                    return (
                      <div key={fwId} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{
                          width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 11, fontWeight: 700,
                          background: isDone ? theme.color.success : isActive ? theme.color.accent : theme.color.border,
                          color: (isDone || isActive) ? "#fff" : theme.color.faint,
                          boxShadow: isActive ? `0 0 0 3px ${theme.color.accent}33` : "none",
                          transition: "all 0.4s",
                        }}>
                          {isDone ? "✓" : isActive ? "→" : ""}
                        </div>
                        <span style={{
                          fontSize: 12,
                          fontWeight: isActive ? 700 : 400,
                          color: isDone ? theme.color.success : isActive ? theme.color.heading : theme.color.faint,
                          transition: "color 0.3s",
                        }}>
                          {label}
                          {isDone && <span style={{ marginLeft: 6, color: theme.color.success, fontSize: 11 }}>done</span>}
                          {isActive && <span style={{ marginLeft: 6, color: theme.color.accentText, fontSize: 11 }}>{passLabel}</span>}
                          {isPending && <span style={{ marginLeft: 6, color: theme.color.faint, fontSize: 11 }}>pending</span>}
                        </span>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {/* Elapsed time + sub-text + cancel */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 12, color: isCancelling ? theme.color.warning : theme.color.subtle }}>
                {isCancelling
                  ? "Cancellation requested — waiting for the AI call to stop…"
                  : isQueued
                  ? "Job is waiting in the queue — you can safely navigate away; it will keep running in the background."
                  : "Analysing your evidence and controls with Claude… You can safely navigate away or log out — the check will keep running and results will be here when you return."}
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0, marginLeft: 16 }}>
                <span style={{ fontSize: 12, color: theme.color.faint, fontVariantNumeric: "tabular-nums" }}>
                  {fmtElapsed(elapsed)}
                </span>
                {!isCancelling && (
                  <button
                    onClick={cancelRun}
                    style={{ fontSize: 11, fontWeight: 600, padding: "4px 10px", borderRadius: 3, border: `1px solid ${theme.color.danger}`, background: "transparent", color: theme.color.dangerText, cursor: "pointer" }}
                  >
                    Cancel
                  </button>
                )}
                {isCancelling && (
                  <button
                    onClick={cancelRun}
                    title="Force-cancel this run immediately (the AI call may still complete in the background)"
                    style={{ fontSize: 11, fontWeight: 600, padding: "4px 10px", borderRadius: 3, border: `1px solid ${theme.color.danger}`, background: theme.color.dangerBg, color: theme.color.dangerText, cursor: "pointer" }}
                  >
                    Force cancel
                  </button>
                )}
              </div>
            </div>

            {/* Worker offline warning (shown after 30 s if no worker) */}
            {workerOffline && (
              <div style={{ marginTop: 14, padding: "10px 14px", background: theme.color.warningBg, border: `1px solid ${theme.color.warning}`, borderRadius: 3 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: theme.color.warningText, marginBottom: 4 }}>
                  Worker appears offline
                </div>
                <div style={{ fontSize: 12, color: theme.color.warningText, lineHeight: 1.6 }}>
                  {queueHealth?.redisUnavailable
                    ? "Cannot reach Redis — the job queue is unavailable. Check that Redis is running and the REDIS_URL environment variable is set."
                    : `No active workers detected. The compliance check worker process is not running. Start it with: npm run dev:worker${queueHealth?.queued ? ` (${queueHealth.queued} job${queueHealth.queued !== 1 ? "s" : ""} waiting in queue)` : ""}.`}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Error from run */}
      {activeRun?.status === "error" && (
        <div style={{ ...card, marginBottom: 20, border: `1px solid ${theme.color.danger}` }}>
          <div style={{ fontSize: 13, color: theme.color.dangerText, marginBottom: 10 }}>
            The compliance check encountered an error: {activeRun.errorMessage ?? "Unknown error"}
          </div>
          <button onClick={() => { setActiveRun(null); resetProgress(); }} style={btnPrimary}>Try again</button>
        </div>
      )}

      {/* Results */}
      {isDone && result && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 20 }}>
          {/* Score + header */}
          <div style={{ ...card, display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
            <div style={{ position: "relative", width: 80, height: 80, flexShrink: 0 }}>
              <svg viewBox="0 0 80 80" style={{ width: 80, height: 80, transform: "rotate(-90deg)" }}>
                <circle cx="40" cy="40" r="34" fill="none" stroke={theme.color.border} strokeWidth="8" />
                <circle
                  cx="40" cy="40" r="34" fill="none"
                  stroke={STATUS_COLORS.COMPLIANT}
                  strokeWidth="8"
                  strokeDasharray={`${2 * Math.PI * 34 * ((result.overallScore ?? 0) / 100)} ${2 * Math.PI * 34}`}
                />
              </svg>
              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 800, color: theme.color.heading }}>
                {result.overallScore ?? "--"}%
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, marginBottom: 6 }}>
                Compliance check complete
              </div>
              <div style={{ fontSize: 11, color: theme.color.subtle, marginBottom: 6 }}>
                The ring is this check's score, partial results counting half. Your Statement of Applicability score, on the
                assessment page and in the sidebar, counts only what is recorded as compliant.
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {/* Every status, so the figures add up to the controls assessed (AUD-39). */}
                {(["COMPLIANT", "PARTIAL", "GAP", "NOT_STARTED"] as const).map((s) => (
                  <span key={s} style={{ fontSize: 12, color: theme.color.muted }}>
                    <span style={{ color: STATUS_COLORS[s], fontWeight: 700 }}>
                      {controls.filter((c) => c.status === s).length}
                    </span>{" "}
                    {SUMMARY_LABELS[s]}
                  </span>
                ))}
              </div>
            </div>
            <button
              onClick={() => { setActiveRun(null); resetProgress(); }}
              style={{ ...btnPrimary, background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`, color: theme.color.muted, fontSize: 12 }}
            >
              Run again
            </button>
          </div>

          {/* Tabs */}
          <div>
            <IndexTabs
              items={[
                { id: "summary", label: "Summary" },
                { id: "gaps", label: "Gaps and partial", count: gapControls.length },
                { id: "all", label: "All Controls", count: controls.length },
              ]}
              value={tab}
              onChange={setTab}
              style={{ marginBottom: 12 }}
            />

            {/* Summary tab */}
            {tab === "summary" && (() => {
              const perStandard: Array<{ fwId: string; text: string }> = (
                result.summaries && Object.keys(result.summaries).length > 0
                  ? Object.entries(result.summaries).map(([fwId, text]) => ({ fwId, text }))
                  : result.summary
                    ? (splitSummaryByFramework(result.summary, controls) ?? [{ fwId: "", text: scopeSummaryText(result.summary, frameworkInScope) }])
                    : []
              ).filter(({ fwId, text }) => text && (!fwId || frameworkInScope(fwId)));

              return (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {perStandard.length > 0 ? perStandard.map(({ fwId, text }) => {
                  return (
                    <div key={fwId || "summary"} style={card}>
                      {fwId && (
                        <div style={{ fontSize: 12, fontWeight: 700, color: theme.color.accentText, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          {standardLabel(fwId, siteNames)}
                        </div>
                      )}
                      <div style={{ fontSize: 13, color: theme.color.text, lineHeight: 1.7 }}>{text}</div>
                    </div>
                  );
                }) : (
                  <div style={card}>
                    <div style={{ fontSize: 13, color: theme.color.muted }}>
                      Overall score: <strong style={{ color: theme.color.heading }}>{result.overallScore ?? "--"}%</strong>
                    </div>
                  </div>
                )}
                {result.keyFindings?.length ? (
                  <div style={card}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 10 }}>Key Findings</div>
                    <ul style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 6 }}>
                      {result.keyFindings.map((f, i) => (
                        <li key={i} style={{ fontSize: 13, color: theme.color.text, lineHeight: 1.6 }}>{f}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
              );
            })()}

            {/* Gaps tab */}
            {tab === "gaps" && (
              gapControls.length === 0 ? (
                <div style={{ ...emptyFrame, padding: "32px 20px", color: theme.color.faint, fontSize: 13 }}>
                  No gaps or partial controls found.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {gapControls.map((c) => {
                    const isExpanded = adviceExpanded.has(adviceKey(c));
                    const isLoadingAdvice = adviceLoading.has(adviceKey(c));
                    const advice = adviceByControl[adviceKey(c)];
                    const fw = frameworkOfResult(c);
                    return (
                      <div key={`${c.controlId}@${c.siteId ?? ""}`} style={card}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
                          <span style={{ fontVariantNumeric: "tabular-nums", fontSize: 11, color: theme.color.faint }}>{c.controlId}</span>
                          {c.name && <span style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading, flex: 1 }}>{c.name}</span>}
                          {fw && (
                            <span style={{ fontSize: 10.5, color: theme.color.muted, background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`, padding: "1px 7px", borderRadius: 2, whiteSpace: "nowrap" }}>
                              {standardLabel(c.siteId ? `${fw}@${c.siteId}` : fw, siteNames)}
                            </span>
                          )}
                          <StatusBadge status={c.status} />
                          {c.carriedFrom && <CarriedFrom date={c.carriedFrom} />}
                          <button
                            onClick={() => navigate(c.siteId ? `/sites/${c.siteId}` : controlRoute(c.controlId, fw))}
                            title="Go to this control's assessment page"
                            style={{ fontSize: 11, color: theme.color.accentText, background: "none", border: "none", cursor: "pointer", padding: "2px 6px", borderRadius: 4, whiteSpace: "nowrap", flexShrink: 0 }}
                          >
                            View →
                          </button>
                        </div>
                        {c.gaps && c.gaps.length > 0 && (
                          <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 4 }}>
                            <strong style={{ color: theme.color.muted }}>Gap:</strong> {c.gaps.join(" · ")}
                          </div>
                        )}
                        {c.policyConflict && (
                          // Your own adopted policy against what the records show.
                          <div style={{ fontSize: 12, color: theme.color.warningText, marginBottom: 4 }}>
                            <strong>Against your policy:</strong> {c.policyConflict}
                          </div>
                        )}
                        {c.recommendations && c.recommendations.length > 0 && (
                          <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 8 }}>
                            <strong style={{ color: theme.color.muted }}>Quick actions:</strong> {c.recommendations.join(" · ")}
                          </div>
                        )}
                        {/* AI advice expand button */}
                        <button
                          onClick={() => toggleAdvice(c)}
                          disabled={isLoadingAdvice}
                          style={{
                            display: "flex", alignItems: "center", gap: 6,
                            background: isExpanded ? theme.color.accentBg : "transparent",
                            border: `1px solid ${isExpanded ? theme.color.accent : theme.color.borderStrong}`,
                            color: isExpanded ? theme.color.accentText : theme.color.muted,
                            borderRadius: 3, padding: "4px 10px", fontSize: 11, fontWeight: 600,
                            cursor: isLoadingAdvice ? "not-allowed" : "pointer",
                            opacity: isLoadingAdvice ? 0.7 : 1,
                          }}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                          </svg>
                          {isLoadingAdvice ? "Loading AI advice…" : isExpanded ? "Hide AI advice" : "Get AI advice"}
                        </button>
                        {isExpanded && (
                          <div style={{ marginTop: 10, padding: "10px 14px", background: theme.color.accentBg, borderRadius: 3, border: `1px solid ${theme.color.accent}44` }}>
                            {isLoadingAdvice ? (
                              <div style={{ fontSize: 12, color: theme.color.accentText }}>Generating tailored advice…</div>
                            ) : advice && advice.length > 0 ? (
                              <>
                                <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.accentText, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                                  AI-recommended actions
                                </div>
                                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                  {advice.map((step, i) => {
                                    const priorityColor =
                                      step.priority === "immediate" ? theme.color.danger
                                      : step.priority === "short-term" ? theme.color.warning
                                      : theme.color.success;
                                    const categoryLabel =
                                      step.category === "policy" ? "[Policy]"
                                      : step.category === "technical" ? "[Technical]"
                                      : step.category === "training" ? "[Training]"
                                      : "[Process]";
                                    return (
                                      <div key={i} style={{ display: "flex", flexDirection: "column", gap: 4, padding: "8px 10px", background: theme.color.panel, borderRadius: 3, border: `1px solid ${theme.color.border}` }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                                          <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 3, background: priorityColor, color: "#fff", textTransform: "capitalize", flexShrink: 0 }}>
                                            {step.priority}
                                          </span>
                                          {step.ownerRole && (
                                            <span style={{ fontSize: 10, fontWeight: 600, padding: "1px 6px", borderRadius: 3, background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`, color: theme.color.muted, flexShrink: 0 }}>
                                              {step.ownerRole}
                                            </span>
                                          )}
                                          {step.effortDays > 0 && (
                                            <span style={{ fontSize: 10, color: theme.color.faint, flexShrink: 0 }}>
                                              ~{step.effortDays} day{step.effortDays !== 1 ? "s" : ""}
                                            </span>
                                          )}
                                          <span style={{ fontSize: 10, color: theme.color.faint, flexShrink: 0 }}>
                                            {categoryLabel}
                                          </span>
                                        </div>
                                        <div style={{ fontSize: 12, color: theme.color.text, lineHeight: 1.6 }}>{step.action}</div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </>
                            ) : (
                              <div style={{ fontSize: 12, color: theme.color.muted }}>
                                Could not generate advice — ensure your service tier includes AI guidance, or try again.
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )
            )}

            {/* All controls tab */}
            {tab === "all" && (
              controls.length === 0 ? (
                <div style={{ ...emptyFrame, padding: "32px 20px", color: theme.color.faint, fontSize: 13 }}>
                  No control data available.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {controls.map((c) => {
                    const fw = frameworkOfResult(c);
                    return (
                    <div key={`${c.controlId}@${c.siteId ?? ""}`} style={{ padding: "10px 14px", background: theme.color.panel, border: `1px solid ${theme.color.border}`, borderRadius: 3, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <span style={{ fontVariantNumeric: "tabular-nums", fontSize: 11, color: theme.color.faint, minWidth: 60 }}>{c.controlId}</span>
                      <span style={{ fontSize: 13, color: theme.color.text, flex: 1 }}>{c.name}</span>
                      {fw && (
                        <span style={{ fontSize: 10.5, color: theme.color.muted, background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`, padding: "1px 7px", borderRadius: 2, whiteSpace: "nowrap" }}>
                          {standardLabel(c.siteId ? `${fw}@${c.siteId}` : fw, siteNames)}
                        </span>
                      )}
                      <StatusBadge status={c.status} />
                          {c.carriedFrom && <CarriedFrom date={c.carriedFrom} />}
                      <button
                        onClick={() => navigate(c.siteId ? `/sites/${c.siteId}` : controlRoute(c.controlId, fw))}
                        title="Go to this control's assessment page"
                        style={{ fontSize: 11, color: theme.color.accentText, background: "none", border: "none", cursor: "pointer", padding: "2px 6px", borderRadius: 4, whiteSpace: "nowrap", flexShrink: 0 }}
                      >
                        View →
                      </button>
                    </div>
                    );
                  })}
                </div>
              )
            )}
          </div>
        </div>
      )}

      {/* Previous runs — always rendered so users can see the section exists */}
      <div style={card}>
        <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 12 }}>Previous runs</div>
        {runsLoadError && (
          <div style={{ fontSize: 12, color: theme.color.dangerText, marginBottom: 8 }}>
            Could not load run history — you may need a higher service tier, or the server is unavailable.
          </div>
        )}
        {!runsLoadError && !runsLoaded && (
          <div style={{ fontSize: 12, color: theme.color.faint, padding: "12px 0" }}>Loading run history…</div>
        )}
        {!runsLoadError && runsLoaded && previousRuns.length === 0 && (
          <div style={{ fontSize: 12, color: theme.color.faint, padding: "12px 0" }}>
            No compliance checks have been run yet. Start one above.
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {previousRuns.map((r) => {
              const canCancel = r.status === "queued" || r.status === "running";
              const isCancellingThis = cancellingIds.has(r.id);
              const badgeStyle: React.CSSProperties = (() => {
                switch (r.status) {
                  case "done":       return { background: theme.color.successBg, color: theme.color.success };
                  case "error":      return { background: theme.color.dangerBg, color: theme.color.danger };
                  case "cancelled":  return { background: "transparent", color: theme.color.subtle };
                  case "cancelling": return { background: theme.color.warningBg, color: theme.color.warningText };
                  case "running":    return { background: theme.color.accentBg, color: theme.color.accentText };
                  case "queued":     return { background: theme.color.border, color: theme.color.muted };
                  default:           return { background: theme.color.border, color: theme.color.muted };
                }
              })();
              return (
                <div
                  key={r.id}
                  onClick={() => r.status === "done" ? setActiveRun(r) : undefined}
                  style={{
                    display: "flex", alignItems: "center", gap: 12, padding: "10px 12px",
                    background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`,
                    borderRadius: 3, cursor: r.status === "done" ? "pointer" : "default",
                  }}
                >
                  <span style={{ fontSize: 12, color: theme.color.faint, flex: 1 }}>
                    {fmtDateTime(r.createdAt)}
                    {r.siteIds && r.siteIds.length > 0 && (
                      <span style={{ marginLeft: 8, color: theme.color.muted }}>
                        Sites: {r.siteIds.map((id) => siteNames.get(id) ?? "removed site").join(", ")}
                      </span>
                    )}
                  </span>
                  {r.overallScore != null && (
                    <span style={{ fontSize: 12, fontWeight: 700, color: theme.color.accentText }}>{r.overallScore}%</span>
                  )}
                  <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 3, ...badgeStyle }}>
                    {RUN_STATUS_LABELS[r.status] ?? r.status}
                  </span>
                  {r.status === "done" && <span style={{ fontSize: 11, color: theme.color.accentText }}>View →</span>}
                  {canCancel && (
                    <button
                      disabled={isCancellingThis}
                      onClick={(e) => { e.stopPropagation(); cancelHistoricalRun(r.id); }}
                      style={{
                        fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 3,
                        border: `1px solid ${theme.color.danger}`,
                        background: "transparent",
                        color: theme.color.dangerText,
                        cursor: isCancellingThis ? "not-allowed" : "pointer",
                        opacity: isCancellingThis ? 0.6 : 1,
                        flexShrink: 0,
                      }}
                    >
                      {isCancellingThis ? "Cancelling…" : "Cancel"}
                    </button>
                  )}
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}

/** Marks a verdict kept from an earlier check because nothing it was judged on had changed. */
function CarriedFrom({ date }: { date: string }) {
  return (
    <span
      title="Nothing this control is judged on changed since this date, so the verdict from that check was kept. Tick “Re-assess every control” to assess it again."
      style={{ fontSize: 10.5, color: theme.color.muted, background: theme.color.panelAlt, border: `1px dashed ${theme.color.border}`, padding: "1px 7px", borderRadius: 2, whiteSpace: "nowrap" }}
    >
      Unchanged since {date}
    </span>
  );
}
